import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Input,
  PageShell,
  Pagination,
  RadioGroup,
  RadixSelect,
  SearchGrid,
  SearchGridItem,
  Skeleton,
  type DataTableColumn,
  type RadioOption,
  type SelectOption,
} from '@/components/ui';
import { Eye, EyeOff } from 'lucide-react';
import { useUserListQuery } from '../hooks/useUserListQuery';
import { useUserGradesQuery } from '../grades/hooks/useUserGradesQuery';
import { buildUserColumns } from '../components/UserTable';
import { UserStatusChangeDialog } from '../components/UserStatusChangeDialog';
import type { AdminMemberSummary, MemberStatus } from '../hooks/useAdminUsersQuery';
import { USER_MALLS } from '../lib/userMall';

const PAGE_SIZE = 20;
const GRADE_FETCH_SIZE = 100;
const ALL_VALUE = '';
const OPT_IN_YES = 'true';
const OPT_IN_NO = 'false';

const buildStatusOptions = (t: (k: string) => string): ReadonlyArray<SelectOption> => [
  { value: ALL_VALUE, label: t('users.filter.statusAll') },
  { value: 'ACTIVE', label: t('users.status.ACTIVE') },
  { value: 'DORMANT', label: t('users.status.DORMANT') },
  { value: 'SUSPENDED', label: t('users.status.SUSPENDED') },
  { value: 'WITHDRAWN', label: t('users.status.WITHDRAWN') },
  // 사업자 회원 승인 게이트 상태 — 이관 승인 대기 11명이 있다. 빠지면 목록에서 걸러 볼 수 없다.
  { value: 'PENDING_APPROVAL', label: t('users.status.PENDING_APPROVAL') },
  { value: 'REJECTED', label: t('users.status.REJECTED') },
];

// 소속 국가몰 — 몰마다 별개 계정이라 아이디/이메일 검색만으로는 동명이인 행이 섞인다. QA-532 라디오(전체|한국몰|…).
const buildMallOptions = (t: (k: string, d: string) => string): ReadonlyArray<RadioOption> => [
  { value: ALL_VALUE, label: t('users.filter.all', '전체') },
  ...USER_MALLS.map((m) => ({ value: m, label: t(`users.mall.${m}`, m) })),
];

// B2B(2026-06-20) — 회원 유형(개인/기업). QA-532 라디오(전체|일반|사업자), 라벨은 users.memberType.* 재사용.
type MemberTypeFilter = 'PERSONAL' | 'BUSINESS';

const buildMemberTypeOptions = (t: (k: string, d: string) => string): ReadonlyArray<RadioOption> => [
  { value: ALL_VALUE, label: t('users.filter.all', '전체') },
  { value: 'PERSONAL', label: t('users.memberType.PERSONAL', '일반') },
  { value: 'BUSINESS', label: t('users.memberType.BUSINESS', '사업자') },
];

// QA-532 수신동의 라디오 — 전체 | 수신 | 수신거부.
const buildOptInOptions = (t: (k: string, d: string) => string): ReadonlyArray<RadioOption> => [
  { value: ALL_VALUE, label: t('users.filter.all', '전체') },
  { value: OPT_IN_YES, label: t('users.filter.optInYes', '수신') },
  { value: OPT_IN_NO, label: t('users.filter.optInNo', '수신거부') },
];

const parseOptIn = (value: string): boolean | null =>
  value === OPT_IN_YES ? true : value === OPT_IN_NO ? false : null;

const parseMileage = (raw: string): number | null => {
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
};

const toOptInValue = (value: boolean | null): string =>
  value === true ? OPT_IN_YES : value === false ? OPT_IN_NO : ALL_VALUE;

const RangeInputs = ({ children }: { children: [React.ReactNode, React.ReactNode] }) => (
  <div className="flex items-center gap-1.5">
    {children[0]}
    <span className="shrink-0 text-[12px]" style={{ color: 'var(--content-muted)' }}>
      ~
    </span>
    {children[1]}
  </div>
);

/**
 * 회원 관리(회원 목록). QA-532 — 검색 조건을 상품 관리와 같은 헤더 | 입력 표(SearchGrid)로.
 * 국가몰은 한 줄 전체, 나머지는 두 쌍씩. 검색어는 [검색]·Enter 로, 나머지 조건은 바꾸면 바로 반영된다.
 */
export const UserListPage = () => {
  const { t } = useTranslation('common');
  const [keyword, setKeyword] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [status, setStatus] = useState<MemberStatus | ''>('');
  const [mallCode, setMallCode] = useState('');
  const [memberType, setMemberType] = useState<MemberTypeFilter | ''>('');
  const [grade, setGrade] = useState('');
  const [joinedFrom, setJoinedFrom] = useState('');
  const [joinedTo, setJoinedTo] = useState('');
  const [lastLoginFrom, setLastLoginFrom] = useState('');
  const [lastLoginTo, setLastLoginTo] = useState('');
  const [emailOptIn, setEmailOptIn] = useState<boolean | null>(null);
  const [smsOptIn, setSmsOptIn] = useState<boolean | null>(null);
  const [marketingOptIn, setMarketingOptIn] = useState<boolean | null>(null);
  const [mileageMin, setMileageMin] = useState<number | null>(null);
  const [mileageMax, setMileageMax] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [masked, setMasked] = useState(false);
  const [statusDialog, setStatusDialog] = useState<{
    userId: string;
    target: MemberStatus;
  } | null>(null);

  const gradesQuery = useUserGradesQuery({ page: 1, size: GRADE_FETCH_SIZE });

  const query = useUserListQuery(
    {
      keyword: keyword || undefined,
      status: status || null,
      mallCode: mallCode || null,
      memberType: memberType || null,
      grade: grade || null,
      joinedFrom: joinedFrom || null,
      joinedTo: joinedTo || null,
      lastLoginFrom: lastLoginFrom || null,
      lastLoginTo: lastLoginTo || null,
      emailOptIn,
      smsOptIn,
      marketingOptIn,
      mileageMin,
      mileageMax,
      page,
      size: PAGE_SIZE,
    },
    !masked,
  );

  const items = query.data?.items ?? [];
  const total = query.data?.meta.total ?? 0;

  const handleSearch = useCallback(() => {
    setKeyword(inputValue);
    setPage(1);
  }, [inputValue]);

  const handleReset = useCallback(() => {
    setInputValue('');
    setKeyword('');
    setStatus('');
    setMallCode('');
    setMemberType('');
    setGrade('');
    setJoinedFrom('');
    setJoinedTo('');
    setLastLoginFrom('');
    setLastLoginTo('');
    setEmailOptIn(null);
    setSmsOptIn(null);
    setMarketingOptIn(null);
    setMileageMin(null);
    setMileageMax(null);
    setPage(1);
  }, []);

  // 조건을 바꾸면 1쪽부터.
  const apply = <V,>(set: (v: V) => void) => (v: V) => {
    set(v);
    setPage(1);
  };

  const statusOptions = useMemo(() => buildStatusOptions(t), [t]);
  const mallOptions = useMemo(() => buildMallOptions(t), [t]);
  const memberTypeOptions = useMemo(() => buildMemberTypeOptions(t), [t]);
  const optInOptions = useMemo(() => buildOptInOptions(t), [t]);

  const gradeOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL_VALUE, label: t('users.filter.gradeAll') },
      ...(gradesQuery.data?.content ?? []).map((g) => ({
        value: g.gradeId,
        label: g.gradeName,
      })),
    ],
    [gradesQuery.data, t],
  );

  // QA-532 No — 단순 열 번호(쪽을 넘겨도 이어진다).
  const columns = useMemo<ReadonlyArray<DataTableColumn<AdminMemberSummary>>>(
    () => [
      {
        key: 'no',
        header: t('users.cols.no', 'No'),
        width: 56,
        align: 'center',
        cell: (_row, index) => (
          <span className="tabular-nums" style={{ color: 'var(--content-muted)' }} data-testid="user-row-no">
            {(page - 1) * PAGE_SIZE + index + 1}
          </span>
        ),
      },
      ...buildUserColumns({
        t,
        onSetStatus: (userId, target) => setStatusDialog({ userId, target }),
      }),
    ],
    [t, page],
  );

  const dateInput = (value: string, onChange: (v: string) => void, label: string) => (
    <Input type="date" value={value} onChange={(e) => apply(onChange)(e.target.value)} aria-label={label} className="w-full" />
  );
  const optInRadio = (value: boolean | null, onChange: (v: boolean | null) => void, label: string, testId: string) => (
    <RadioGroup
      value={toOptInValue(value)}
      onValueChange={(v) => apply(onChange)(parseOptIn(v))}
      options={optInOptions}
      orientation="horizontal"
      aria-label={label}
      data-testid={testId}
    />
  );

  return (
    <PageShell
      title={t('users.list.title')}
      data-testid="user-list-page"
      actions={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMasked((v) => !v)}
          aria-label={masked ? t('users.filter.unmask') : t('users.filter.mask')}
        >
          {masked ? (
            <>
              <EyeOff size={14} className="mr-1" />
              {t('users.filter.masked')}
            </>
          ) : (
            <>
              <Eye size={14} className="mr-1" />
              {t('users.filter.unmasked')}
            </>
          )}
        </Button>
      }
    >
      <SearchGrid
        aria-label={t('users.filter.label', '회원 검색 조건')}
        testId="user-search-grid"
        className="mb-4"
        footer={
          <>
            {/* QA-532 [검색] 빨간색, [초기화]는 예전 검색 버튼 모양(테두리). */}
            <Button variant="cta" size="sm" onClick={handleSearch} data-testid="user-search">
              {t('users.filter.search')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleReset} data-testid="user-search-reset">
              {t('users.filter.reset', '초기화')}
            </Button>
          </>
        }
      >
        <SearchGridItem label={t('users.filter.mallCode', '국가몰')} span="full">
          <RadioGroup
            value={mallCode}
            onValueChange={apply(setMallCode)}
            options={mallOptions}
            orientation="horizontal"
            aria-label={t('users.filter.mallCode', '국가몰')}
            data-testid="user-filter-mall"
          />
        </SearchGridItem>

        <SearchGridItem label={t('users.filter.keyword', '검색어')} htmlFor="user-keyword">
          <Input
            id="user-keyword"
            type="search"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder={t('users.filter.keywordPlaceholder')}
            aria-label={t('users.filter.keywordPlaceholder')}
          />
        </SearchGridItem>
        <SearchGridItem label={t('users.cols.status', '상태')}>
          <RadixSelect
            value={status}
            onChange={(v) => apply(setStatus)(v as MemberStatus | '')}
            options={statusOptions}
            aria-label={t('users.cols.status', '상태')}
          />
        </SearchGridItem>

        <SearchGridItem label={t('users.cols.memberType', '회원 유형')}>
          <RadioGroup
            value={memberType}
            onValueChange={(v) => apply(setMemberType)(v as MemberTypeFilter | '')}
            options={memberTypeOptions}
            orientation="horizontal"
            aria-label={t('users.cols.memberType', '회원 유형')}
            data-testid="user-filter-member-type"
          />
        </SearchGridItem>
        <SearchGridItem label={t('users.filter.grade')}>
          <RadixSelect
            value={grade}
            onChange={apply(setGrade)}
            options={gradeOptions}
            disabled={gradesQuery.isPending}
            aria-label={t('users.filter.grade')}
          />
        </SearchGridItem>

        <SearchGridItem label={t('users.filter.joinedAt', '가입일')}>
          <RangeInputs>
            {dateInput(joinedFrom, setJoinedFrom, t('users.filter.joinedFrom'))}
            {dateInput(joinedTo, setJoinedTo, t('users.filter.joinedTo'))}
          </RangeInputs>
        </SearchGridItem>
        <SearchGridItem label={t('users.filter.lastLoginAt', '최종로그인일')}>
          <RangeInputs>
            {dateInput(lastLoginFrom, setLastLoginFrom, t('users.filter.lastLoginFrom'))}
            {dateInput(lastLoginTo, setLastLoginTo, t('users.filter.lastLoginTo'))}
          </RangeInputs>
        </SearchGridItem>

        <SearchGridItem label={t('users.filter.smsOptIn')}>
          {optInRadio(smsOptIn, setSmsOptIn, t('users.filter.smsOptIn'), 'user-filter-sms')}
        </SearchGridItem>
        <SearchGridItem label={t('users.filter.emailOptIn')}>
          {optInRadio(emailOptIn, setEmailOptIn, t('users.filter.emailOptIn'), 'user-filter-email')}
        </SearchGridItem>

        <SearchGridItem label={t('users.filter.marketingOptIn')}>
          {optInRadio(marketingOptIn, setMarketingOptIn, t('users.filter.marketingOptIn'), 'user-filter-kakao')}
        </SearchGridItem>
        <SearchGridItem label={t('users.filter.mileageRange')}>
          <RangeInputs>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={mileageMin ?? ''}
              onChange={(e) => apply(setMileageMin)(parseMileage(e.target.value))}
              placeholder={t('users.filter.mileageMin')}
              aria-label={t('users.filter.mileageMin')}
              className="w-full"
            />
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={mileageMax ?? ''}
              onChange={(e) => apply(setMileageMax)(parseMileage(e.target.value))}
              placeholder={t('users.filter.mileageMax')}
              aria-label={t('users.filter.mileageMax')}
              className="w-full"
            />
          </RangeInputs>
        </SearchGridItem>
      </SearchGrid>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('users.list.loadFailed')}</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('users.actions.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...items]}
          rowKey={(row) => row.userId}
          stickyHeader
          mobileCardCollapse
          emptyState={t('users.list.empty')}
          ariaLabel={t('users.list.title')}
        />
      )}

      <Pagination
        total={total}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />

      {statusDialog ? (
        <UserStatusChangeDialog
          open
          onOpenChange={(o) => {
            if (!o) setStatusDialog(null);
          }}
          userId={statusDialog.userId}
          targetStatus={statusDialog.target}
          onSuccess={() => setStatusDialog(null)}
        />
      ) : null}
    </PageShell>
  );
};
